import VueRouter from "vue-router";

import { PushNotificationProvider } from "@/models";
import { Auth } from "@/utils/auth";
import { NativeAppType } from "./";
import { BaseNativeApp, CallNative, WebToNativeMethodParameter } from "./baseNativeApp";

export const TrustedDeviceSecretStorageKey = "Messenger_TrustedDeviceSecret";

export default class Web extends BaseNativeApp {
  readonly isMobile = false;
  readonly type = NativeAppType.Web;
  readonly deviceOS: string;
  readonly pushNotificationProvider = PushNotificationProvider.None;

  trustedDeviceSecret?: string;

  readonly callNative: CallNative = (method, param) => {
    /*
     * Override native app functionality here so that any logic in the BaseNativeApp's methods can still be called.
     * For example, for saveTrustedDeviceSecret(), the BaseNativeApp doesn't save the secret if the user isn't logged
     * in and it also lowercases the email to ensure a match, even if the user entered the email with capital letters.
     */

    if (method === "saveTrustedDeviceSecret") {
      const p = param as WebToNativeMethodParameter<"saveTrustedDeviceSecret">;
      this._saveTrustedDeviceSecret(p.email, p.secret);
      return;
    }

    if (method === "getTrustedDeviceSecret") {
      const p = param as WebToNativeMethodParameter<"getTrustedDeviceSecret">;
      this._getTrustedDeviceSecret(p.email);
      return;
    }

    // Since this is the web, just log the API call
    console.debug(`NATIVE APP: calling ${method}`, param);
  };

  constructor(router: VueRouter, auth: Auth) {
    super(router, auth);

    this.deviceOS = this.getOperatingSystem();
    this.deviceMake = this.getBrowser();
  }

  /**
   * Save the device secret by the user's email to local storage so that browsers can be trusted.
   * @param email the email that the secret belongs to
   * @param secret the device secret that will be used to bypass 2FA
   */
  private _saveTrustedDeviceSecret(email: string, secret: string) {
    const key = this.getTrustedDeviceSecretKeyForUser(email);
    localStorage.setItem(key, secret);
  }

  /**
   * Get the device secret for the user's email and pass it back to the BaseNativeApp's pending callback.
   * @param email the email that the secret belongs to
   */
  private _getTrustedDeviceSecret(email: string) {
    const key = this.getTrustedDeviceSecretKeyForUser(email);
    const secret = localStorage.getItem(key);

    // If secret is null replace it with undefined to match the parameter's type
    this.sendGetTrustedDeviceSecretResponse({ secret: secret ?? undefined });
  }

  private getTrustedDeviceSecretKeyForUser(email: string): string {
    return TrustedDeviceSecretStorageKey + "_" + email;
  }

  getOperatingSystem(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.indexOf("Win") != -1) return "Windows";
    if (userAgent.indexOf("Mac") != -1) return "Mac";
    if (userAgent.indexOf("Linux") != -1) return "Linux";
    if (userAgent.indexOf("Android") != -1) return "Android";
    if (userAgent.indexOf("like Mac") != -1) return "iOS";

    return "Unknown OS";
  }

  getBrowser(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.indexOf("Opera") != -1 || userAgent.indexOf("OPR") != -1) return "Opera";
    if (userAgent.indexOf("Edg") != -1) return "Edge";
    if (userAgent.indexOf("Chrome") != -1) return "Chrome";
    if (userAgent.indexOf("Safari") != -1) return "Safari";
    if (userAgent.indexOf("Firefox") != -1) return "Firefox";
    if (userAgent.indexOf("MSIE") != -1) return "IE";

    return "Browser";
  }
}
